import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import { UserDependentStore } from './UserDependentStore'

import { DEBUG, DEBUG_WARN, ShoppingCartApi } from '@/util'
import { IDeliveryAddressDTO, IOrderingData, IShoppingCartDTO } from '@/types/shoppingCart'

/** store of current shopping cart content */
export const useShoppingCartStore = defineStore('shoppingCartStore', () => {
    const api = new ShoppingCartApi

    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _shoppingCart: Ref<IShoppingCartDTO | null> = ref(null)
    const _orderData: Ref<IOrderingData> = ref(createEmptyOrderData())
    const state = { _shoppingCart, _orderData }

    /********************************************************************************************************************
     PRIVATE FUNCTION
     ********************************************************************************************************************/
    function createEmptyOrderData(): IOrderingData {
        return {
            comment: '',
            reference: '',
            deliveryAddress: {
                name1: '',
                name2: '',
                streetName: '',
                houseNumber: '',
                zipCode: '',
                cityName: '',
                countryCode: '',
                attention: '',
            } as IDeliveryAddressDTO
        }
    }

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    const shoppingCart = computed(() => _shoppingCart.value)
    const orderData = computed(() => _orderData.value)

    const getters = { shoppingCart, orderData }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Set a new delivery address */
    function setOrderData(orderData: IOrderingData) {
        _orderData.value = orderData
    }

    /** Clear (reset) the shopping cart */
    function clear() {
        return api.reset()
            .then(response => {
                _shoppingCart.value = response.data
                resetOrderData()
            })
    }

    /** Update cart */
    function update(materialNumber: number, count: number) {
        return api.update(materialNumber, count)
            .then(response => _shoppingCart.value = response.data)
    }

    /** Update cart (serial number item) */
    function updateSerialNumbers(materialNumber: number, serialNumbers: string[]) {
        return api.updateSerialNumbers(materialNumber, serialNumbers)
            .then(response => _shoppingCart.value = response.data)
    }

    /** Commit cart */
    function commit(orderData: IOrderingData) {
        return api.commit(orderData)
            .then(response => {
                _shoppingCart.value = response.data
                resetOrderData()
            })
    }

    /** Send cart by email */
    function sendByEmail() {
        return api.sendByEmail()
            .then(response => {
                _shoppingCart.value = response.data
                resetOrderData()
            })
    }

    function resetOrderData() {
        _orderData.value = createEmptyOrderData()
    }

    const actions = { setOrderData, clear, update, updateSerialNumbers, commit, sendByEmail, resetOrderData }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    function loadShoppingCart() {
        DEBUG('ShoppingCartStore: Fetching cart data...')
        api.get()
            .then(response => _shoppingCart.value = response.data)
            .catch(error => DEBUG_WARN('ShoppingCartStore: Failed to load cart data:', error))
    }

    function resetShoppingCart() {
        DEBUG('ShoppingCartStore: Reset')
        _shoppingCart.value = null
    }

    new UserDependentStore(() => loadShoppingCart(), () => resetShoppingCart())

    /********************************************************************************************************************
     PUBLIC INTERFACE
     ********************************************************************************************************************/
    return { ...state, ...getters, ...actions }
})